import { Input } from "@legal-500/legal500-design-system";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import {
  BackgroundField,
  BlockItemList,
  RichTextField,
  useSyncBlockContent,
  useUploadTracker,
} from "./blockEditorParts";
import type { PeopleContent } from "./types";
import styles from "../CMS.module.css";

type PeopleEditorProps = {
  value: PeopleContent;
  onChange: (value: PeopleContent) => void;
  onUploadingChange?: (uploading: boolean) => void;
};

export function PeopleEditor({ value, onChange, onUploadingChange }: PeopleEditorProps) {
  const { control, register, watch } = useForm<PeopleContent>({ defaultValues: value });
  const { fields, append, remove, move } = useFieldArray({ control, name: "people" });
  const { uploading, report } = useUploadTracker(onUploadingChange);
  useSyncBlockContent(watch, value, onChange);

  return (
    <div className={styles.cardEditorFields}>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="people-heading">
          Heading
        </label>
        <Input id="people-heading" {...register("heading")} />
      </div>
      <RichTextField control={control} name="intro" id="people-intro" label="Intro" />

      <BlockItemList
        noun="Person"
        keys={fields.map(field => field.id)}
        disabled={uploading}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => append({ name: "", role: "", email: "", imageUrl: "" })}
        renderItem={(index, key) => (
          <>
            <div className={styles.cardEditorFieldRow}>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`people-name-${key}`}>
                  Name <span className={styles.required}>*</span>
                </label>
                <Input
                  id={`people-name-${key}`}
                  required
                  pattern=".*\S.*"
                  title="Enter a name"
                  {...register(`people.${index}.name`)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`people-role-${key}`}>
                  Role
                </label>
                <Input id={`people-role-${key}`} {...register(`people.${index}.role`)} />
              </div>
            </div>
            <div className={styles.cardEditorFieldRow}>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`people-email-${key}`}>
                  Email
                </label>
                <Input
                  id={`people-email-${key}`}
                  type="email"
                  {...register(`people.${index}.email`)}
                />
              </div>
              <div className={styles.formField}>
                <Controller
                  name={`people.${index}.imageUrl`}
                  control={control}
                  render={({ field }) => (
                    <CmsImageUploadField
                      value={field.value}
                      previewAlt="Person photo"
                      cardLabel={`Person ${index + 1}: ${watch(`people.${index}.name`) || "Untitled"}`}
                      onChange={field.onChange}
                      onUploadingChange={pending => report(key, pending)}
                    />
                  )}
                />
              </div>
            </div>
          </>
        )}
      />

      <BackgroundField register={register} name="background" stored={value.background} />
    </div>
  );
}
